import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { CurrencyPropType } from './PaymentTransactionPropTypes';
import { QUALIFICATION_TYPE } from '../ReduxStorage/PortalAdmin/constants';

export const PortalTraitTemplatePropType = ImmutablePropTypes.mapContains({
  iconImageUrl: PropTypes.string,
  content: PropTypes.string,
  template: PropTypes.string,
});

export const PortalPageSectionPropType = ImmutablePropTypes.mapContains({
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.any,
  hidden: PropTypes.bool,
});

export const PortalPageSectionsDataPropType = ImmutablePropTypes.listOf(
  PortalPageSectionPropType
);

export const PortalFooterDataPropType = ImmutablePropTypes.listOf(
  ImmutablePropTypes.mapContains({
    title: PropTypes.string.isRequired,
    links: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  })
);

export const PortalFaqDataPropType = ImmutablePropTypes.mapContains({
  mongo_id: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
});

export const PortalFaqsDataPropType = ImmutablePropTypes.listOf(
  PortalFaqDataPropType
);

export const PortalTeamMemberDataPropType = ImmutablePropTypes.mapContains({
  profileName: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
});

export const PortalTeamDataPropType = ImmutablePropTypes.listOf(
  PortalTeamMemberDataPropType
);

export const CourseOutcomePropType = ImmutablePropTypes.mapContains({
  description: PropTypes.string.isRequired,
});

export const PortalOutcomesDataPropType = ImmutablePropTypes.mapContains({
  anchor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  outcomes: ImmutablePropTypes.listOf(CourseOutcomePropType.isRequired),
});

const CommunityImagesPropType = ImmutablePropTypes.listOf(
  ImmutablePropTypes.mapContains({
    avatar: PropTypes.string.isRequired,
  })
);

export const PortalCommunityDataPropType = ImmutablePropTypes.mapContains({
  users: CommunityImagesPropType.isRequired,
  usersCount: PropTypes.number.isRequired,
  commentsCount: PropTypes.number.isRequired,
  countriesCount: PropTypes.number,
});

export const PortalInstitutionPropType = ImmutablePropTypes.mapContains({
  media: ImmutablePropTypes.mapContains({
    logoImage: PropTypes.string,
    promoVideoUrl: PropTypes.string,
    promoImageUrl: PropTypes.string,
  }),
  footer: PortalFooterDataPropType.isRequired,
});

export const InfoTemplate = ImmutablePropTypes.mapContains({
  template: PropTypes.string,
  icon: PropTypes.string,
  content: PropTypes.string,
});

// TODO: change the props back from optional to required once we work out the data structure
export const PortalTopBarLinkPropType = ImmutablePropTypes.mapContains({
  title: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
});

export const PortalCoursePropType = ImmutablePropTypes.mapContains({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  promoImage: PropTypes.string,
  blurb: PropTypes.string,
  timeCommitment: PropTypes.string,
  objective: PropTypes.string,
  outcomes: ImmutablePropTypes.listOf(CourseOutcomePropType.isRequired),
  isOffline: PropTypes.bool,

  // creator info
  creator: ImmutablePropTypes.mapContains({
    fullName: PropTypes.string,
    'avatar@3x': PropTypes.string,
  }).isRequired,

  // cohort info
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  paidCohort: PropTypes.bool,
  studentPrice: PropTypes.number,
  currency: CurrencyPropType,

  studentSummary: ImmutablePropTypes.mapContains({
    totalStudents: PropTypes.number,
    totalComments: PropTypes.number,
    randomUsers: CommunityImagesPropType,
  }),

  // promo data
  videoUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  summary: PropTypes.string,
  introduction: PropTypes.string,
  furtherInformation: PropTypes.string,
  content: PropTypes.string,
  topBarLinks: ImmutablePropTypes.listOf(PortalTopBarLinkPropType),
  pageSections: ImmutablePropTypes.listOf(PortalPageSectionPropType),
  overlayColour: PropTypes.string,
  overlayOpacity: PropTypes.number,
  CpdHours: PropTypes.number,
  hideCost: PropTypes.bool,
});

export const PortalQualificationPropType = ImmutablePropTypes.mapContains({
  path: PropTypes.string.isRequired,
  qualificationCode: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  bannerImageUrl: PropTypes.string,
  promoVideoUrl: PropTypes.string,
  enumtype: PropTypes.oneOf(QUALIFICATION_TYPE).isRequired,
  code: PropTypes.string,
  title: PropTypes.string.isRequired,
  introduction: PropTypes.string.isRequired,
  studyMode: PropTypes.string.isRequired,
  timeEstimate: PropTypes.string.isRequired,
  price: PropTypes.number,
  studyStyle: PropTypes.string,
  uoc: PropTypes.number,
  coreCourses: ImmutablePropTypes.listOf(PortalCoursePropType),
  electiveCourses: ImmutablePropTypes.listOf(PortalCoursePropType),
  requiredElectives: PropTypes.number,
  isFreeToLearn: PropTypes.bool,
  certifiedByTemplate: PortalTraitTemplatePropType,
  recognisedTemplate: PortalTraitTemplatePropType,
  priceTemplate: PortalTraitTemplatePropType,
  studyModeTemplate: PortalTraitTemplatePropType,
  durationTemplate: PortalTraitTemplatePropType,
  startDateTemplate: PortalTraitTemplatePropType,
  endDateTemplate: PortalTraitTemplatePropType,
  isEnrolled: PropTypes.bool,
});

export const PortalAdminQualificationPropType = ImmutablePropTypes.mapContains({
  path: PropTypes.string.isRequired,
  enumtype: PropTypes.oneOf(QUALIFICATION_TYPE).isRequired,
  code: PropTypes.string,
  title: PropTypes.string.isRequired,
  introduction: PropTypes.string.isRequired,
  studyMode: PropTypes.string.isRequired,
  timeEstimate: PropTypes.string.isRequired,
  price: PropTypes.number,
  studyStyle: PropTypes.string,
  uoc: PropTypes.number,
  coreCourses: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  electiveCourses: ImmutablePropTypes.listOf(PropTypes.string),
  requiredElectives: PropTypes.number,
  isFreeToLearn: PropTypes.bool,
});

export const PortalQualificationsDataPropType = ImmutablePropTypes.listOf(
  PortalQualificationPropType
);

export const PortalCourseLinksPropType = ImmutablePropTypes.listOf(
  PortalTopBarLinkPropType
);

export const PortalTestimonialPropType = ImmutablePropTypes.mapContains({
  photoImage: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  testimonial: PropTypes.string.isRequired,
});

export const PortalTestimonialsDataPropType = ImmutablePropTypes.listOf(
  PortalTestimonialPropType
);

export const PortalSellingPointPropType = ImmutablePropTypes.mapContains({
  imageUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
});

export const PortalSellingPointsDataPropType = ImmutablePropTypes.listOf(
  PortalSellingPointPropType
);

export const PortalCourseCategoryPropType = ImmutablePropTypes.mapContains({
  image: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  blurb: PropTypes.string.isRequired,
});

export const PortalCourseCategoriesDataPropType = ImmutablePropTypes.listOf(
  PortalCourseCategoryPropType
);
